import React, { useEffect, useRef, useState } from 'react';
import { Animated, AppState, ColorValue, Easing, Platform, Pressable, ScrollView, useWindowDimensions, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faAward, faBookOpen, faClock, faGift, faStore, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import useTranslation from '../../../hooks/translation-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import { scenarioReviewRemaining, ScenarioNotice, MissionReviewSummary } from '../../../helpers/scenarioReview';
import { formatMissionCurrency } from '../../../helpers/missionCompensation';
import { useAppSelector } from '../../../hooks/store-hook';
import MissionListLogo from '../../mymission/MissionListLogo';
import CustomButton from '../CustomButton';
import Label from '../Label';
import MissionPanelPage from './MissionPanelPage';
import type { QualityWarningSummaryDto } from '../../../api/api.g';
import { useQualityWarning } from '../../../hooks/useQualityWarning';
import QualityWarningContent from '../qualityWarning/QualityWarningContent';

export interface AcceptanceReviewContent {
  missionName: string;
  summary?: MissionReviewSummary;
  scenario: string;
  instructions: string;
  notice?: ScenarioNotice;
  qualityWarning?: QualityWarningSummaryDto;
  manualConfirmation?: boolean;
}

export interface AcceptanceReviewPresentation {
  presentation?: 'page' | 'footer' | 'embedded';
  onReadInstructions?(): void;
  textColor?: ColorValue;
  accentColor?: ColorValue;
  errorColor?: ColorValue;
  confirmColor?: ColorValue;
}

interface Props extends AcceptanceReviewContent, AcceptanceReviewPresentation {
  onClose(): void;
  onConfirm(): Promise<void>;
  onNavigate?(): void;
}

const MissionAcceptanceReview = ({ missionName, summary, scenario, notice, qualityWarning, manualConfirmation, onClose, onConfirm, onNavigate,
  presentation = 'page', onReadInstructions, textColor, accentColor, errorColor, confirmColor }: Props) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const quality = useQualityWarning();
  const [manual] = useState(!!manualConfirmation);
  useEffect(() => { if (qualityWarning?.id) quality.displayed(qualityWarning.id); }, [qualityWarning?.id, quality.displayed]);
  const { width, height } = useWindowDimensions();
  const language = useAppSelector(state => state.userPrefs.uiLanguage);
  const accent = (accentColor ?? theme.text.titleDark).toString();
  const [deadline] = useState(() => Date.now() + 10000);
  const countdownProgress = useRef(new Animated.Value(1)).current;
  const [remaining, setRemaining] = useState(10);
  const [countdownPrefix, countdownSuffix] = t('scenario-review-countdown', { seconds: '__seconds__' }).split('__seconds__');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const confirming = useRef(false);
  const cancelled = useRef(false);
  const automaticAttempted = useRef(false);
  const hasScenario = !!scenario.trim();
  const updated = !!notice?.isActive;
  const subtitle = summary?.subtitle?.trim();
  const showSubtitle = !!subtitle && subtitle.toLowerCase() !== missionName.trim().toLowerCase();
  const hasSalaryPoints = typeof summary?.salaryPoints === 'number' && Number.isFinite(summary.salaryPoints);
  const title = updated ? 'scenario-review-updated-title' : hasScenario ? 'scenario-review-generic-title' : 'scenario-review-instructions-title';
  const body = updated ? 'scenario-review-updated-body' : hasScenario ? 'scenario-review-generic-body' : 'scenario-review-instructions-body';
  const close = () => { cancelled.current = true; countdownProgress.stopAnimation(); onClose(); };
  const readInstructions = () => { close(); onReadInstructions?.(); };

  const confirm = async () => {
    if (cancelled.current || confirming.current) return;
    if ((AppState.currentState && AppState.currentState !== 'active') ||
      (Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'hidden')) {
      close();
      return;
    }
    automaticAttempted.current = true;
    countdownProgress.stopAnimation();
    confirming.current = true;
    setBusy(true);
    setFailed(false);
    try { await onConfirm(); } catch { if (!cancelled.current) setFailed(true); }
    finally { confirming.current = false; if (!cancelled.current) setBusy(false); }
  };
  const confirmation = useRef(confirm);
  confirmation.current = confirm;

  useEffect(() => {
    cancelled.current = false;
    if (manual) return () => { cancelled.current = true; countdownProgress.stopAnimation(); };
    const duration = Math.max(0, deadline - Date.now());
    countdownProgress.setValue(Math.min(1, duration / 10000));
    const animation = Animated.timing(countdownProgress, {
      toValue: 0, duration, easing: Easing.linear,
      useNativeDriver: Platform.OS !== 'web', isInteraction: false,
    });
    animation.start();
    const tick = () => {
      if (cancelled.current || automaticAttempted.current) return;
      const seconds = scenarioReviewRemaining(deadline, Date.now());
      setRemaining(seconds);
      if (seconds === 0) void confirmation.current();
    };
    tick();
    const timer = setInterval(tick, 100);
    return () => { cancelled.current = true; clearInterval(timer); animation.stop(); };
  }, [deadline, countdownProgress, manual]);

  const countdown = !manual && !busy && !failed && <View accessibilityLiveRegion="polite" style={{ gap: presentation === 'footer' ? 6 : 10 }}>
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
      <FontAwesomeIcon icon={faClock} size={16} color={accent} style={{ marginTop: 2 }} />
      <Label numberOfLines={0} style={{ flexShrink: 1, color: accent, lineHeight: 20 }}>
        {countdownPrefix}<Label numberOfLines={0} style={{ fontWeight: '700', color: accent, fontVariant: ['tabular-nums'] }}>{remaining}</Label>{countdownSuffix}
      </Label>
    </View>
    <View testID="mission-review-countdown-track" accessible={false} importantForAccessibility="no-hide-descendants"
      style={{ height: 3, borderRadius: 3, overflow: 'hidden', backgroundColor: theme.background.missionDetailSummary }}>
      <Animated.View testID="mission-review-countdown-fill" style={{ height: '100%', width: '100%', backgroundColor: theme.primary,
        transformOrigin: 'left center', transform: [{ scaleX: countdownProgress }] }} />
    </View>
  </View>;
  const failure = failed && <Label numberOfLines={0} accessibilityRole="alert" style={{ color: errorColor ?? theme.text.error }}>{t('Something-went-wrong-please-try-again-later')}</Label>;
  const readLink = <Pressable testID="mission-review-read-instructions"
    accessibilityRole="button" accessibilityLabel={t('scenario-review-read-instructions')} onPress={readInstructions} disabled={busy}
    style={{ minHeight: 44, alignItems: 'center', justifyContent: 'center', opacity: busy ? 0.5 : 1 }}>
    <Label numberOfLines={0} style={{ color: accent, textDecorationLine: 'underline', textAlign: 'center', lineHeight: 20 }}>
      {t('scenario-review-read-instructions')}
    </Label>
  </Pressable>;
  const confirmButton = <CustomButton testID="mission-review-confirm" title={t(manual && qualityWarning && !qualityWarning.acknowledgedAtUtc ? 'quality-warning-acknowledge-accept' : 'scenario-review-confirm-read')} accessibilityRole="button"
    accessibilityLabel={t(manual && qualityWarning && !qualityWarning.acknowledgedAtUtc ? 'quality-warning-acknowledge-accept' : 'scenario-review-confirm-read')} accessibilityState={{ disabled: busy, busy }} disabled={busy} onPress={confirm}
    style={{ width: '100%', minHeight: 48, paddingHorizontal: 20,
      ...(confirmColor != null ? { backgroundColor: confirmColor, borderColor: confirmColor } : {}) }} contentStyle={{ flex: 0, height: 'auto' }}
    textStyle={{ color: 'white', textAlign: 'center', flexShrink: 1 }} />;

  if (presentation === 'footer') return <MissionPanelPage missionName={missionName} page="review" presentation="footer" onBack={close} onNavigate={onNavigate}>
    <View testID="mission-review-footer" style={{ gap: 8, minWidth: 0 }}>
      {qualityWarning && <ScrollView testID="mission-review-quality-warning" keyboardShouldPersistTaps="handled"
        style={{ maxHeight: Math.max(100, height * 0.22), backgroundColor: '#FFF0D5', borderRadius: 8 }} contentContainerStyle={{ padding: 12 }}>
        <QualityWarningContent compact={!!qualityWarning.acknowledgedAtUtc} />
      </ScrollView>}
      {(updated || !qualityWarning) && <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
        <FontAwesomeIcon icon={updated ? faTriangleExclamation : faBookOpen} size={18} color={updated ? '#F5A21B' : accent} style={{ marginTop: 1 }} />
        <Label numberOfLines={0} style={{ fontSize: 14, lineHeight: 20, fontWeight: '700', color: accent, flexShrink: 1 }}>{t(title)}</Label>
      </View>}
      {failure}
      {countdown}
      <View testID="mission-review-actions" style={{ flexDirection: width < 768 ? 'column-reverse' : 'row', flexWrap: 'wrap', alignItems: 'center', gap: width < 768 ? 0 : 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 16, width: width < 768 ? '100%' : undefined, flexShrink: 1 }}>
          {readLink}
          <Pressable testID="mission-review-cancel" accessibilityRole="button" accessibilityLabel={t('cancel')} onPress={close} disabled={busy}
            style={{ minHeight: 44, justifyContent: 'center', paddingHorizontal: 8, opacity: busy ? 0.5 : 1 }}>
            <Label style={{ color: accent }}>{t('cancel')}</Label>
          </Pressable>
        </View>
        <View style={{ width: width < 768 ? '100%' : 320, maxWidth: '100%', marginLeft: width < 768 ? 0 : 'auto' }}>{confirmButton}</View>
      </View>
    </View>
  </MissionPanelPage>;

  const missionSummary = summary && <View style={{ marginTop: 16, flexDirection: 'row', alignItems: 'center', gap: 12, minWidth: 0 }}>
    <View style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 24, alignItems: 'center', justifyContent: 'center',
      backgroundColor: theme.background.secondary, overflow: 'hidden' }}>
      <MissionListLogo logoUrl={summary.logoUrl} imageStyle={{ width: 48, height: 48 }}
        defaultLogo={<FontAwesomeIcon icon={faStore} size={24} color={accent} />} />
    </View>
    <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
      <Label accessibilityRole="header" numberOfLines={0} style={{ fontSize: 18, lineHeight: 24, fontWeight: '700' }}>{missionName}</Label>
      {showSubtitle && <Label numberOfLines={0} style={{ fontSize: 13, lineHeight: 18 }}>{subtitle}</Label>}
    </View>
  </View>;

  const embedded = presentation === 'embedded';
  return <MissionPanelPage missionName={missionName} summary={missionSummary || undefined} page="review" presentation={presentation} onBack={close} onNavigate={onNavigate}>
    <View testID="mission-review-card" style={embedded ? { minWidth: 0 } : { marginTop: 16, borderRadius: 8, overflow: 'hidden', backgroundColor: theme.background.missionDetail }}>
      {!embedded && summary && <View testID="mission-review-reward" style={{ padding: 16, gap: 10, backgroundColor: theme.background.missionDetailSummary }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <FontAwesomeIcon icon={faAward} size={20} color={accent} />
          <Label numberOfLines={0} style={{ fontSize: 22, lineHeight: 28, fontWeight: '700', color: accent }}>
            {summary.pointsOnly ? t('points-value', { value: hasSalaryPoints ? summary.salaryPoints : 0 }) : formatMissionCurrency(summary.salaryAmount, language)}
          </Label>
          <Label numberOfLines={0} style={{ fontSize: 13, fontWeight: '400', color: accent }}>{t('salary')}</Label>
          {!summary.pointsOnly && hasSalaryPoints && <Label numberOfLines={0} style={{ color: accent }}>({summary.salaryPoints} pts)</Label>}
        </View>
        {summary.promotionDisplay?.promotions?.map(promotion => <View key={promotion.periodMissionConfigurationId}
          style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
          <FontAwesomeIcon icon={faGift} size={18} color={accent} style={{ marginTop: 1 }} />
          <Label numberOfLines={0} style={{ color: accent, flexShrink: 1, fontSize: 13, lineHeight: 18 }}>{promotion.label}</Label>
        </View>)}
      </View>}
      <View style={embedded ? { paddingHorizontal: 16, paddingTop: 4, gap: 12 } : { padding: 16, gap: 12 }}>
        {qualityWarning && <View testID="mission-review-quality-warning" style={{ backgroundColor: '#FFF0D5', borderRadius: 8, padding: 12 }}>
          <QualityWarningContent compact={!!qualityWarning.acknowledgedAtUtc} />
        </View>}
        {(updated || !qualityWarning) && <View style={{ gap: 8 }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
            <FontAwesomeIcon icon={updated ? faTriangleExclamation : faBookOpen} size={22}
              color={updated ? '#F5A21B' : accent} style={{ flexShrink: 0, marginTop: 1 }} />
            <Label accessibilityRole="header" numberOfLines={0}
              style={{ color: accent, fontSize: 16, lineHeight: 22, fontWeight: '700', flex: 1, minWidth: 0 }}>{t(title)}</Label>
          </View>
          {!qualityWarning && <Label numberOfLines={0} style={{ lineHeight: 22, color: textColor ?? theme.text.primary }}>{t(body)}</Label>}
        </View>}
        {failure}
        <View style={{ minWidth: 0, gap: 16 }}>
          {countdown}
          <View testID="mission-review-actions" style={{ width: embedded || width < 768 ? '100%' : 320, maxWidth: '100%', alignSelf: 'center', gap: 8 }}>
            {readLink}
            {confirmButton}
          </View>
        </View>
      </View>
    </View>
  </MissionPanelPage>;
};

export default MissionAcceptanceReview;
